import {diff} from 'deep-object-diff';

import {useRef} from 'react';
import {DeepPartial, FieldValues, UseFormReturn} from 'react-hook-form';

import {
  // eslint-disable-next-line no-restricted-imports -- Intended
  assocPath,
  concat,
  defaultTo,
  flip,
  head,
  last,
  not,
  // eslint-disable-next-line no-restricted-imports -- Intended
  path,
  pipe,
} from 'ramda';
import {isFunction, isNilOrEmpty, isString} from 'ramda-adjunct';

import {handleApiError} from '@dms/shared';
import {objectPaths, removeKeys, filterElementsBySuffixMatch} from '@dms/warehouse/shared';

import {ApiError, Nullish, sanitizeObject} from 'shared/web';

const ABORT_ERROR = 'AbortError';

export interface FormRecalculationConfig<TFormData extends FieldValues, TRequestBody, TResponse> {
  /**
   * Array of allowed field names that can trigger recalculation
   */
  allowedFields: string[];

  /**
   * Array of allowed object names that contain the allowed fields
   */
  allowedObjects: string[];

  /**
   * API mutation function for recalculating prices
   */
  recalculateMutation: (params: {
    body: TRequestBody;
    signal: AbortSignal;
    [key: string]: unknown;
  }) => {
    unwrap: () => Promise<TResponse>;
  };

  /**
   * Request config
   */
  requestConfig: {
    [key: string]: unknown;
  };

  /**
   * Function to build the API request body from form values and changed field
   */
  buildRequestBody: (
    formValues: DeepPartial<TFormData>,
    changedField: string,
    changedFieldWithFullPath?: string
  ) => TRequestBody;

  /**
   * Function to transform the API response into new form values
   */
  transformResponse: (
    response: TResponse,
    currentFormValues: DeepPartial<TFormData>
  ) => DeepPartial<TFormData>;

  /**
   * Function to check if the difference is empty based on allowed objects
   */
  isDifferenceEmpty: (allowedDifference: any) => boolean;

  /**
   * Optional function to map field names from frontend to backend
   */
  mapFieldName?: (changedField: string, changedFieldWithFullPath?: string) => string;
}

export function useFormRecalculation<TFormData extends FieldValues, TRequestBody, TResponse>(
  config: FormRecalculationConfig<TFormData, TRequestBody, TResponse>
) {
  const abortControllerInstance = useRef<AbortController | Nullish>(null);
  const shouldSkipRecalculation = useRef(false);
  const prevFormValues = useRef<DeepPartial<TFormData>>({} as DeepPartial<TFormData>);

  const handleChange = (
    values: DeepPartial<TFormData>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    change: any,
    formApi: UseFormReturn<TFormData>
  ) => {
    // Skip recalculation if we're in the middle of resetting the form
    if (shouldSkipRecalculation.current) {
      return;
    }

    const copiedValues = structuredClone(values) as object;
    const {asString} = pipe(
      defaultTo({}),
      flip(diff)(sanitizeObject(copiedValues)),
      objectPaths
    )(sanitizeObject(prevFormValues.current));

    // Name of the changed field
    const changedField = last<string>(change.name?.split('.') ?? []);
    const changedFieldWithFullPath = change.name;

    // Indicate if the changed field is allowed to trigger recalculation
    const shouldTriggerRecalculation = config.allowedFields.includes(changedField);

    // Get the difference between the previous and current form values
    const difference = diff(sanitizeObject(prevFormValues.current), sanitizeObject(copiedValues));

    // Difference that contains only allowed values
    const allowedDifference = removeKeys(
      difference,
      concat(config.allowedFields, config.allowedObjects)
    );

    const isDiffEmpty = config.isDifferenceEmpty(allowedDifference);

    if (not(shouldTriggerRecalculation) || isNilOrEmpty(allowedDifference) || isDiffEmpty) {
      return;
    }

    const arrayOfChangedValues = asString.includes('.') ? asString.split(',') : asString;
    const pathToChangedValues =
      head(filterElementsBySuffixMatch(arrayOfChangedValues, config.allowedFields))?.split('.') ??
      [];
    const changedValue = path(pathToChangedValues ?? [], values);
    const newPrevFormValues = assocPath(
      pathToChangedValues ?? [],
      changedValue,
      prevFormValues.current
    );

    prevFormValues.current = newPrevFormValues;

    // Abort previous request if it exists
    if (abortControllerInstance.current && isFunction(abortControllerInstance.current.abort)) {
      abortControllerInstance.current.abort();
    }

    const calculateController = new AbortController();
    abortControllerInstance.current = calculateController;

    // Map field name if mapping function is provided
    const mappedFieldName = config.mapFieldName
      ? config.mapFieldName(changedField, changedFieldWithFullPath)
      : changedField;

    // Build request body
    const requestBody = config.buildRequestBody(values, mappedFieldName, changedValue as string);

    // Prepare mutation parameters
    const mutationParams = {
      ...config.requestConfig,
      body: requestBody,
      signal: calculateController.signal,
    };

    config
      .recalculateMutation(mutationParams)
      .unwrap()
      .then((response) => {
        const newValues = config.transformResponse(response, values);

        prevFormValues.current = newValues;
        shouldSkipRecalculation.current = true;
        formApi.reset(newValues as TFormData, {keepDirty: true});
        shouldSkipRecalculation.current = false;
      })
      .catch((error: {error: Error}) => {
        // If the request is aborted, then we don't want to show the error notification
        if (
          error &&
          'error' in error &&
          isString(error.error) &&
          error.error.includes(ABORT_ERROR)
        ) {
          return;
        }
        handleApiError(error as ApiError);
      });
  };

  const setPrevFormValues = (values: DeepPartial<TFormData>) => {
    prevFormValues.current = values;
  };

  return {
    handleChange,
    setPrevFormValues,
    shouldSkipRecalculation,
  };
}
